import { AlertTriangle, ArrowRight, RefreshCw, ShoppingCart, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fdate, mxn } from "@/lib/format";
import { EPS_OT } from "./OtEstado";

const opciones = (ot) => [
  { k: "compra", titulo: "Comprar el bien", icon: ShoppingCart,
    efecto: `El arrendatario paga el valor residual ${mxn(ot.residual)} + IVA ${mxn(ot.iva_residual)} = ${mxn(ot.total_residual)}. `
      + "El crédito queda Liquidado y se emite la carta de transmisión de propiedad." },
  { k: "prorroga", titulo: "Prorrogar el arrendamiento", icon: RefreshCw,
    efecto: `Se envía al Comité de Crédito un nuevo arrendamiento por el residual ${mxn(ot.residual)} SIN IVA, con póliza de seguro `
      + "nueva y vigente. Si lo autoriza, el nuevo crédito se contrata solo (sin desembolso) y este queda Liquidado con residual «Prorrogada»." },
  { k: "devolucion", titulo: "Devolver el bien", icon: Undo2,
    efecto: `El arrendatario entrega el bien con el acta de entrega-recepción firmada. El residual ${mxn(ot.residual)} queda `
      + "«Cancelada por devolución», el crédito Liquidado y el bien pasa a «Bienes devueltos» de Cartera." },
];

const bloqueoDe = (ot, k) => {
  if (ot.estado === "en_comite") return k === "prorroga" ? "Ya hay una prórroga en el Comité de Crédito." : ot.bloqueo;
  if (ot.diferidos > EPS_OT) return `Primero cubre los moratorios diferidos de las rentas (${mxn(ot.diferidos)}) desde «Diferidos por cobrar».`;
  if (ot.mora_total > EPS_OT) return `Primero paga la mora del residual (${mxn(ot.mora_total)}).`;
  return null;
};

function TarjetaOpcion({ o, bloqueo, onElegir }) {
  const Icon = o.icon;
  return (
    <div data-testid={`ot-opcion-${o.k}`}
      className={`rounded-lg border p-3 transition-colors ${bloqueo ? "border-slate-200 bg-slate-50" : "border-slate-200 bg-white hover:border-primary/50"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Icon className="h-4 w-4 text-primary" /> {o.titulo}</p>
          <p className="mt-1 text-xs leading-relaxed text-slate-600" data-testid={`ot-opcion-${o.k}-efecto`}>{o.efecto}</p>
          {bloqueo && <p className="mt-1 text-xs font-medium text-rose-700" data-testid={`ot-opcion-${o.k}-bloqueo`}>{bloqueo}</p>}
        </div>
        <Button size="sm" variant="outline" className="shrink-0" disabled={!!bloqueo} data-testid={`ot-opcion-${o.k}-btn`} onClick={() => onElegir(o.k)}>
          Elegir <ArrowRight className="ml-1 h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// Elección de la opción terminal: cada opción dice su consecuencia; si hay mora, primero se paga solo la mora + IVA.
export function OtElegirDialog({ open, onOpenChange, ot, onElegir }) {
  const conMora = ot.mora_total > EPS_OT && ot.estado !== "en_comite";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="ot-elegir-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Opción terminal · {ot.numero_credito}</DialogTitle>
          <DialogDescription>Las rentas están cubiertas. Elige cómo se cierra el arrendamiento; cada opción muestra su resumen y
            pide confirmación antes de ejecutarse.</DialogDescription>
        </DialogHeader>
        {conMora && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800" data-testid="ot-elegir-mora">
            <p className="flex items-start gap-2"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>Fecha límite vencida ({fdate(ot.fecha_limite)}, {ot.dias_vencidos} días). Primero se paga solo la mora del residual
                + IVA: <b>{mxn(ot.mora_total)}</b>. Se aplica únicamente a la mora; el residual sigue pendiente.</span></p>
            <Button size="sm" className="mt-2" data-testid="ot-elegir-mora-btn" onClick={() => onElegir("mora")}>Pagar mora {mxn(ot.mora_total)}</Button>
          </div>
        )}
        <div className="space-y-2">
          {opciones(ot).map((o) => <TarjetaOpcion key={o.k} o={o} bloqueo={bloqueoDe(ot, o.k)} onElegir={onElegir} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
}
